import React, { useState, useEffect } from "react";
import { Form, Input } from "antd";
import { EyeInvisibleOutlined, EyeOutlined, EyeTwoTone } from "@ant-design/icons";

export default function AuthOForm(props) {
  const [domainName, setDomainName] = useState("");
  const [clientID, setClientID] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [domainNameError, setDomainNameError] = useState(false);
  const [clientIDError, setClientIDError] = useState(false);
  const [clientSecretError, setClientSecretError] = useState(false);

  const validateDomain = (domain) => {
    if (!domain || !domain.trim()) return false;
    const trimmedDomain = domain.trim();

    // Must contain at least one dot (e.g., domain.com, not just "domain")
    if (!trimmedDomain.includes(".")) return false;

    // Check for valid domain format with proper structure
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

    // Additional checks
    const parts = trimmedDomain.split(".");
    if (parts.length < 2) return false;
    if (parts.some((part) => part.length === 0)) return false;
    if (parts[parts.length - 1].length < 2) return false;

    return domainRegex.test(trimmedDomain);
  };

  const validateClientID = (clientId) => {
    if (!clientId || !clientId.trim()) return false;
    const trimmed = clientId.trim();
    // Must not be empty and contain only alphanumeric, hyphens, and underscores
    return trimmed.length > 0 && /^[a-zA-Z0-9_-]+$/.test(trimmed);
  };

  const validateClientSecret = (secret) => {
    if (!secret || !secret.trim()) return false;
    // Must be at least 6 characters
    return secret.trim().length >= 6;
  };

  // Initialize values from props when component mounts or props change
  useEffect(() => {
    if (props.connectionData && props.connectionData.authInfo && props.connectionData.authInfo.parameters) {
      const authParams = props.connectionData.authInfo.parameters;
      const newDomainName = authParams.domainName || "";
      const newClientID = authParams.clientID || "";
      const newClientSecret = authParams.clientSecret || "";

      setDomainName(newDomainName);
      setClientID(newClientID);
      setClientSecret(newClientSecret);

      // Update parent component with existing values
      props.handleChangeValues({
        domainName: newDomainName,
        clientID: newClientID,
        clientSecret: newClientSecret,
      });
    }
  }, [props.connectionData]);

  // Validation effect - runs whenever any field changes
  useEffect(() => {
    const isDomainValid = validateDomain(domainName);
    const isClientIdValid = validateClientID(clientID);
    const isSecretValid = validateClientSecret(clientSecret);

    // ALL fields must be valid for the form to be valid
    const isValid = isDomainValid && isClientIdValid && isSecretValid;

    // Only show errors if the field has been touched (not empty)
    setDomainNameError(!isDomainValid && domainName.trim() !== "");
    setClientIDError(!isClientIdValid && clientID.trim() !== "");
    setClientSecretError(!isSecretValid && clientSecret.trim() !== "");

    // Notify parent component of validation status
    if (props.onValidationChange) {
      props.onValidationChange(isValid);
    }
  }, [domainName, clientID, clientSecret, props.onValidationChange]);

  const handleChangeDomainName = (e) => {
    const newDomainName = e.target.value;
    setDomainName(newDomainName);

    props.handleChangeValues({
      domainName: newDomainName,
      clientID: clientID,
      clientSecret: clientSecret,
    });
  };

  const handleChangeClientID = (e) => {
    const newClientID = e.target.value;
    setClientID(newClientID);

    props.handleChangeValues({
      domainName: domainName,
      clientID: newClientID,
      clientSecret: clientSecret,
    });
  };

  const handleChangeClientSecret = (e) => {
    const newClientSecret = e.target.value;
    setClientSecret(newClientSecret);

    props.handleChangeValues({
      domainName: domainName,
      clientID: clientID,
      clientSecret: newClientSecret,
    });
  };

  return (
    <div>
      <Form.Item
        style={{ marginBottom: "8px" }}
        label="Auth Domain"
        rules={[{ required: true, message: "Please input Auth Domain!" }]}
        required={true}
        validateStatus={domainNameError ? "error" : ""}
        help={domainNameError ? "Please enter a valid domain (e.g., your-domain.auth0.com)" : ""}
      >
        <Input
          value={domainName}
          onChange={handleChangeDomainName}
          disabled={props.isDisabled || props.isViewOnly}
          placeholder="your-domain.auth0.com"
        />
      </Form.Item>

      <Form.Item
        style={{ marginBottom: "8px" }}
        label="Auth Client ID"
        rules={[{ required: true, message: "Please input Auth Client ID!" }]}
        required={true}
        validateStatus={clientIDError ? "error" : ""}
        help={clientIDError ? "Client ID must contain only alphanumeric characters, hyphens, and underscores" : ""}
      >
        <Input
          value={clientID}
          onChange={handleChangeClientID}
          disabled={props.isViewOnly}
          placeholder="Enter client ID"
        />
      </Form.Item>

      <Form.Item
        style={{ marginBottom: "8px" }}
        label="Auth Client Secret"
        rules={[{ required: true, message: "Please input Auth Client Secret!" }]}
        required={true}
        validateStatus={clientSecretError ? "error" : ""}
        help={clientSecretError ? "Client secret must be at least 6 characters long" : ""}
      >
        <Input.Password
          value={clientSecret}
          onChange={handleChangeClientSecret}
          disabled={props.isViewOnly}
          placeholder="Enter client secret"
          visibilityToggle={!props.isViewOnly && !props.isDisabled}
          iconRender={(visible) =>
            clientSecret.length > 0 ? visible ? <EyeInvisibleOutlined /> : <EyeOutlined /> : null
          }
        />
      </Form.Item>
    </div>
  );
}
